import { Empty_NormalRangeExpression } from './consts.js';
import { __stringify, _keys, _values, exists, filterInstances, MUSTNeverHappenException, StaticLogger } from '@nu-art/ts-common';
import { resolveValueType } from '../_entity.js';
import { LogicType_And, LogicType_Complex, LogicType_Equals, LogicType_In, LogicType_Self } from '../_enum.js';
/**
 * Converts a bulk of variable-value couplings into expressions.
 * Iterates over each coupling to convert them individually and collects conversion outcomes.
 *
 * @param dependencyMapper A mapping of dependencies to facilitate conversion.
 * @param dataMappers An object containing mappings to assist with value and variable resolution.
 * @returns An object containing arrays of successfully converted expressions and variables that failed conversion.
 */
export const valueToExpressionBulk = (dependencyMapper, dataMappers) => {
    const convertedExpressions = [];
    const failedToConvert = [];
    _keys(dependencyMapper).forEach(key => {
        dependencyMapper[key].forEach(coupling => {
            const expression = valueToExpressionSingle(coupling, dataMappers, key);
            if (expression)
                return convertedExpressions.push(expression);
            if (coupling.variable)
                failedToConvert.push(coupling.variable);
        });
    });
    return { expressions: convertedExpressions, failedToConvert: failedToConvert };
};
/**
 * Converts a single variable-value coupling into an expression.
 * Determines the conversion function based on the dependency amount.
 *
 * @param coupling The coupling of a variable and its corresponding value to be converted.
 * @param dataMappers An object containing mappings for values and variables to assist with conversion.
 * @param _dependencyAmount The amount of dependencies as a string, which influences the conversion logic.
 * @returns A UI_Expression representing the converted expression, or undefined if conversion fails.
 */
export const valueToExpressionSingle = (coupling, dataMappers, _dependencyAmount) => {
    const dependencyAmount = exists(_dependencyAmount) ? Number(_dependencyAmount) : coupling.value.data.declarationVarIds.length;
    switch (dependencyAmount) {
        case 0:
            return convertCoupling(coupling, dataMappers, convertToNoDependencyExpression);
        case 1:
            return convertCoupling(coupling, dataMappers, convertToSingleAtom);
        default:
            return convertCoupling(coupling, dataMappers, convertToSubTerms);
    }
};
/**
 * A wrapper function to handle conversion of a coupling to an expression, logging errors if they occur.
 *
 * @param coupling The variable-value coupling to convert.
 * @param dataMappers Mappings for resolving variables and values during conversion.
 * @param conversionFunction The specific conversion logic to use based on the coupling context.
 * @returns A UI_Expression representing the converted expression, or undefined if an error occurs.
 */
const convertCoupling = (coupling, dataMappers, conversionFunction) => {
    try {
        return conversionFunction(coupling, dataMappers);
    }
    catch (err) {
        StaticLogger.logErrorBold(`error in variable ${coupling.variable?.name ?? 'No var name'}`, err);
    }
};
/**
 * Converts a coupling into an expression with no dependencies.
 *
 * @param coupling The variable-value coupling to convert.
 * @param dataMappers Not used in this context but included for consistency in the conversion function interface.
 * @returns A UI_Expression with no dependencies.
 * @throws MUSTNeverHappenException If more than one normal range is present without dependencies.
 */
const convertToNoDependencyExpression = (coupling, dataMappers) => {
    if (coupling.value.data.normalValues.length > 1)
        throw new MUSTNeverHappenException('cannot implement more than one normal range without variable dependencies');
    const normalRangeExpression = Empty_NormalRangeExpression(coupling.value, coupling.variable);
    normalRangeExpression.expression = `${__stringify(coupling.value.data.normalValues[0].range)}`;
    return normalRangeExpression;
};
/**
 * Converts a coupling into an expression with a single atom.
 *
 * @param coupling The variable-value coupling to convert.
 * @param dataMappers Mappings for resolving variables and values to construct cases for the atom.
 * @returns A UI_Expression with a single atom derived from the coupling's value.
 */
const convertToSingleAtom = (coupling, dataMappers) => {
    const normalRangeExpression = Empty_NormalRangeExpression(coupling.value, coupling.variable);
    const variableId = coupling.value.data.declarationVarIds[0];
    let elseRange = [0, 0];
    const cases = filterInstances(coupling.value.data.normalValues.map(normalRange => {
        const value = dataMappers.valueMap[_values(normalRange.conditions)[0]];
        const variable = dataMappers.variableMap[variableId];
        const variableValue = dataMappers.valueMap[variable.valueId];
        if (!_keys(normalRange.conditions).length) {
            elseRange = normalRange.range;
            return;
        }
        return {
            rightHand: value?._id ? [value._id] : [],
            value: normalRange.range,
            caseOperator: variableValue.type === 'range' ? LogicType_In : LogicType_Equals
        };
    }));
    normalRangeExpression.terms = [{
            expression: 'A1',
            atoms: [{
                    leftHand: {
                        type: 'variable',
                        id: variableId
                    },
                    cases: cases,
                    fallbackValue: elseRange
                }],
        }];
    return normalRangeExpression;
};
/**
 * Converts a coupling into an expression with multiple sub-terms, each potentially containing multiple atoms.
 *
 * @param coupling The variable-value coupling to convert.
 * @param dataMappers Mappings for resolving variables and values, crucial for creating the complex structure of sub-terms.
 * @returns A UI_Expression representing a complex expression derived from the coupling's value.
 */
const convertToSubTerms = (coupling, dataMappers) => {
    const normalRangeExpression = Empty_NormalRangeExpression(coupling.value, coupling.variable);
    const atoms = coupling.value.data.declarationVarIds.map(varId => ({
        leftHand: {
            type: 'variable',
            id: varId
        },
        cases: [{
                caseOperator: LogicType_Self,
                rightHand: [],
                valueType: resolveValueType({ type: 'variable', id: varId }, dataMappers)
            }]
    }));
    let elseCase = [0, 0];
    const subTerms = filterInstances(coupling.value.data.normalValues.map(normalRange => {
        if (!_keys(normalRange.conditions).length) {
            elseCase = normalRange.range;
            return;
        }
        return {
            expression: _keys(normalRange.conditions).map((condition, index) => `A${index + 1}`).join(` ${LogicType_And} `),
            atomsLogic: LogicType_And,
            atoms: filterInstances(_keys(normalRange.conditions).map(variableId => {
                const atomId = coupling.value.data.declarationVarIds.indexOf(variableId);
                const variable = dataMappers.variableMap[variableId];
                const variableValue = dataMappers.valueMap[variable.valueId];
                if (atomId === -1)
                    return;
                return {
                    leftHand: {
                        type: 'atom',
                        id: String(atomId + 1)
                    },
                    cases: [{
                            rightHand: [normalRange.conditions[variableId]],
                            caseOperator: variableValue.type === 'range' ? LogicType_In : LogicType_Equals,
                        }]
                };
            })),
            value: normalRange.range
        };
    }));
    normalRangeExpression.terms = [{
            expression: 'A1',
            atomsLogic: LogicType_Complex,
            subTerms: subTerms,
            fallbackValue: elseCase,
            value: [0, 0],
            atoms: atoms
        }];
    return normalRangeExpression;
};
//# sourceMappingURL=valueToExpression.js.map